/* ==========================================
   TEMA CUSTOM — theme.js
   Utilidades globales + componentes core
   ========================================== */

/* ---- Utilidades ---- */

/**
 * Atrapa el foco dentro de un elemento (modales, drawers).
 * Retorna función para liberar el foco.
 */
function trapFocus(element) {
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const focusableEls = element.querySelectorAll(FOCUSABLE);
  if (!focusableEls.length) return function() {};

  const first = focusableEls[0];
  const last = focusableEls[focusableEls.length - 1];

  function handler(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  }

  element.addEventListener('keydown', handler);
  first.focus();

  return function releaseFocus() {
    element.removeEventListener('keydown', handler);
  };
}

/**
 * Debounce: retrasa la ejecución hasta que pase el delay sin llamadas.
 */
function debounce(fn, delay) {
  if (delay === undefined) delay = 300;
  var timer;
  return function() {
    var context = this;
    var args = arguments;
    clearTimeout(timer);
    timer = setTimeout(function() { fn.apply(context, args); }, delay);
  };
}

/**
 * Throttle: ejecuta como máximo una vez cada `limit` ms.
 */
function throttle(fn, limit) {
  if (limit === undefined) limit = 100;
  var inThrottle = false;
  return function() {
    var context = this;
    var args = arguments;
    if (!inThrottle) {
      fn.apply(context, args);
      inThrottle = true;
      setTimeout(function() { inThrottle = false; }, limit);
    }
  };
}

/**
 * Fetch wrapper para Shopify AJAX API.
 */
async function shopifyFetch(url, options) {
  if (!options) options = {};
  var response = await fetch(url, Object.assign({}, options, {
    headers: Object.assign({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }, options.headers || {})
  }));
  if (!response.ok) throw new Error('HTTP ' + response.status);
  return response.json();
}


/* ---- Overlay global ---- */

var overlay = document.querySelector('[data-overlay]');

function showOverlay() {
  if (overlay) {
    overlay.hidden = false;
    document.body.style.overflow = 'hidden';
  }
}

function hideOverlay() {
  if (overlay) {
    overlay.hidden = true;
    document.body.style.overflow = '';
  }
}

if (overlay) {
  overlay.addEventListener('click', function() {
    document.dispatchEvent(new CustomEvent('drawer:close'));
    hideOverlay();
  });
}


/* ---- Header ---- */

(function() {
  var header = document.querySelector('[data-header]');
  if (!header) return;

  /* -- Sticky header -- */
  if (header.classList.contains('header--sticky')) {
    var sentinel = document.querySelector('[data-header-sentinel]');
    if (sentinel && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function(entries) {
        header.classList.toggle('header--scrolled', !entries[0].isIntersecting);
      }, { threshold: 0 });
      observer.observe(sentinel);
    }
  }

  /* -- Mega menú (desktop) -- */
  var megaToggles = document.querySelectorAll('[data-mega-toggle]');
  var activeMega = null;
  var closeTimer = null;

  function openMega(toggle) {
    closeMega();
    var panel = toggle.nextElementSibling;
    if (!panel) return;
    toggle.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    activeMega = { toggle: toggle, panel: panel };
  }

  function closeMega() {
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    if (!activeMega) return;
    activeMega.toggle.setAttribute('aria-expanded', 'false');
    activeMega.panel.hidden = true;
    activeMega = null;
  }

  megaToggles.forEach(function(toggle) {
    var parent = toggle.closest('.site-nav__item');
    var panel = toggle.nextElementSibling;
    if (!panel) return;

    // Hover intent (desktop)
    parent.addEventListener('mouseenter', function() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      openMega(toggle);
    });

    parent.addEventListener('mouseleave', function() {
      closeTimer = setTimeout(closeMega, 200);
    });

    // Click toggle
    toggle.addEventListener('click', function(e) {
      e.preventDefault();
      if (activeMega && activeMega.toggle === toggle) {
        closeMega();
      } else {
        openMega(toggle);
      }
    });

    // Keyboard: Enter/Space opens, Escape closes
    toggle.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (activeMega && activeMega.toggle === toggle) {
          closeMega();
          toggle.focus();
        } else {
          openMega(toggle);
          // Focus first link inside mega menu
          var firstLink = panel.querySelector('a');
          if (firstLink) firstLink.focus();
        }
      }
      if (e.key === 'Escape') {
        closeMega();
        toggle.focus();
      }
    });
  });

  // Escape cierra mega menú desde cualquier contexto
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && activeMega) {
      var returnFocus = activeMega.toggle;
      closeMega();
      returnFocus.focus();
    }
  });


  /* -- Mobile menu (drawer) -- */
  var mobileMenu = document.querySelector('[data-mobile-menu]');
  var mobileToggle = document.querySelector('[data-mobile-menu-toggle]');
  var mobileClose = document.querySelector('[data-mobile-menu-close]');
  var releaseMobileFocus = null;

  function openMobileMenu() {
    if (!mobileMenu) return;
    mobileMenu.hidden = false;
    mobileMenu.classList.add('is-open');
    mobileToggle.setAttribute('aria-expanded', 'true');
    showOverlay();
    releaseMobileFocus = trapFocus(mobileMenu);
  }

  function closeMobileMenu() {
    if (!mobileMenu) return;
    mobileMenu.classList.remove('is-open');
    mobileMenu.hidden = true;
    mobileToggle.setAttribute('aria-expanded', 'false');
    hideOverlay();
    if (releaseMobileFocus) { releaseMobileFocus(); releaseMobileFocus = null; }
    mobileToggle.focus();
  }

  if (mobileToggle) {
    mobileToggle.addEventListener('click', function() {
      if (mobileMenu.hidden) {
        openMobileMenu();
      } else {
        closeMobileMenu();
      }
    });
  }

  if (mobileClose) {
    mobileClose.addEventListener('click', closeMobileMenu);
  }

  // Escape cierra mobile menu
  if (mobileMenu) {
    mobileMenu.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') closeMobileMenu();
    });
  }

  // Cerrar al escuchar evento global
  document.addEventListener('drawer:close', closeMobileMenu);

  /* -- Submenús mobile (accordion) -- */
  var subMenuToggles = mobileMenu ? mobileMenu.querySelectorAll('[data-submenu-toggle]') : [];
  subMenuToggles.forEach(function(btn) {
    btn.addEventListener('click', function() {
      var submenu = btn.nextElementSibling;
      var isOpen = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!isOpen));
      if (submenu) submenu.hidden = isOpen;
    });
  });


})();

/* ---- Badge del carrito + Quick Add (globales, siempre activos) ---- */
(function() {
  document.addEventListener('cart:updated', function(e) {
    var badges = document.querySelectorAll('[data-cart-count]');
    var count = (e.detail && e.detail.items_count) || 0;
    badges.forEach(function(badge) {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  });

  var SVG_CART = '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zm3.102 2.5 1.313 7h8.17l1.313-7H3.102zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/></svg>';
  var SVG_CHECK = '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/></svg>';

  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn || btn.dataset.loading) return;

    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    btn.dataset.loading = '1';
    btn.innerHTML = SVG_CHECK;

    shopifyFetch('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    })
    .then(function() {
      // Incrementar badge sin segunda petición a /cart.js
      document.querySelectorAll('[data-cart-count]').forEach(function(badge) {
        var n = (parseInt(badge.textContent, 10) || 0) + 1;
        badge.textContent = n;
        badge.hidden = false;
      });
      document.dispatchEvent(new CustomEvent('cart:added'));
      setTimeout(function() {
        btn.innerHTML = SVG_CART;
        delete btn.dataset.loading;
      }, 1500);
    })
    .catch(function() {
      btn.innerHTML = SVG_CART;
      delete btn.dataset.loading;
    });
  });

  /* ---- Scroll suave para anclas internas (#seccion y /#seccion) ----
     Soluciona que al volver a tocar un enlace cuya sección ya está en el
     hash, el navegador ignore el salto. Siempre hace scroll a la sección.
     Respeta el scroll-margin-top de cada sección (header fijo). */
  document.addEventListener('click', function(e) {
    var link = e.target.closest('a[href]');
    if (!link) return;

    // Debe tener un fragmento (#algo)
    if (!link.hash || link.hash === '#') return;

    // Mismo sitio y misma página (ej: estamos en "/" y el link es "/#categorias")
    if (link.host !== window.location.host) return;
    if (link.pathname !== window.location.pathname) return;

    var id = decodeURIComponent(link.hash.slice(1));
    var target = document.getElementById(id);
    if (!target) return; // la sección no está en esta página: comportamiento normal

    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Refleja el hash en la URL sin provocar un segundo salto
    if (window.history && history.replaceState) {
      history.replaceState(null, '', link.pathname + link.search + link.hash);
    }
  }, false);
})();
